// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { Row, Col, Button, GSGrid, View } from "@components";
import { AddFileAttachmentModal } from "@modules/shared/components";
import type { State } from "@store/reducers";
import type { AlertEmailAttachment } from "@modules/service/reducer/alerts/types";

type OwnProps = $Exact<{
  clientId: number,
  alertId: number,
  data: Array<AlertEmailAttachment>,
  schema: Object,
  onChange: (attachments: Array<AlertEmailAttachment>) => void,
}>;

type PropsFromState = $Exact<{
  showAddModal: boolean,
  showDeleteModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setShowAddModal: typeof actionCreators.SetShowAddAlertAttachmentModal,
  setShowDeleteModal: typeof actionCreators.SetShowDeleteAlertAttachmentModal,
  addAttachment: typeof actionCreators.AddAlertAttachment,
  deleteAttachment: typeof actionCreators.DeleteAlertAttachment,
  downloadAttachment: typeof actionCreators.DownloadAlertAttachment,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AlertEmailAttachmentsForm extends React.Component<Props> {
  downloadAttachment = (fileId: number, fileName: string) => () => {
    const { clientId, alertId } = this.props;

    this.props.downloadAttachment({ clientId, alertId, fileId, fileName });
  };

  get columns() {
    return [
      {
        name: this.props.t("ALERT_DETAILS_EMAIL_ATTACHMENT_ID"),
        key: "id",
      },
      {
        name: this.props.t("ALERT_DETAILS_EMAIL_ATTACHMENT_NAME"),
        key: "name",
        align: "left",
      },
      {
        name: "",
        key: "downloadAttachment",
        align: "center",
        width: 100,
        renderCell: ({ row }: { row: AlertEmailAttachment }) => {
          return (
            (!row.isArchived && (
              <div className="d-flex justify-content-center">
                <span
                  className="material-icons pointer mr-4"
                  onClick={this.downloadAttachment(row.id, row.name)}
                >
                  file_download
                </span>
                <span
                  className="material-icons font-size-26 pointer"
                  onClick={this.handleDelete(row.id)}
                >
                  delete
                </span>
              </div>
            )) || <div />
          );
        },
      },
    ];
  }

  get data() {
    return this.props.data || [];
  }

  handleShowAdd = () => {
    this.props.setShowAddModal(true);
  };

  handleDelete = (id: number) => () => {
    this.props.deleteAttachment({
      clientId: this.props.clientId,
      alertId: this.props.alertId,
      attachmentId: id,
    });
  };

  handleCloseAddModal = () => {
    this.props.setShowAddModal(false);
  };

  handleSubmitAddModal = (form: Object) => {
    this.props.addAttachment({ clientId: this.props.clientId, alertId: this.props.alertId, form });
  };

  render() {
    return (
      <View gap={3} className="gs-container">
        <View direction="row" justifyContent="space-between" alignItems="center" flex={1}>
          <h6>{this.props.t("ATTACHMENTS")}</h6>
          <Button className="link record-table__add-button" onClick={this.handleShowAdd}>
            <span className="material-icons font-size-26 mr-1">add</span>
            {this.props.t("ALERT_DETAILS_ADD_EMAIL_ATTACHMENT")}
          </Button>
        </View>

        {this.data ? (
          <GSGrid
            columns={this.columns}
            rows={this.data}
            noResultsText={this.props.t("NO_ATTACHMENTS_MESSAGE")}
          />
        ) : null}
        {this.props.showAddModal && (
          <AddFileAttachmentModal
            title="ALERT_DETAILS_ADD_EMAIL_ATTACHMENT"
            clientId={this.props.clientId}
            onCancel={this.handleCloseAddModal}
            onSubmit={this.handleSubmitAddModal}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  showAddModal: serviceSelectors.getShowAlertAddAttachmentModal(state),
  showDeleteModal: serviceSelectors.getShowAlertDeleteAttachmentModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setShowAddModal: actionCreators.SetShowAddAlertAttachmentModal,
  setShowDeleteModal: actionCreators.SetShowDeleteAlertAttachmentModal,
  addAttachment: actionCreators.AddAlertAttachment,
  deleteAttachment: actionCreators.DeleteAlertAttachment,
  downloadAttachment: actionCreators.DownloadAlertAttachment,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AlertEmailAttachmentsForm);
